/*
  lp-bundle.js : Bundle & Save landing page (Oct 2026).

  1) window.LPBundle, the shared cart contract (also used by the kit pages):
     LPBundle.add(items, opts)  items = [{id: variantId, quantity: 1}]. POSTs /cart/add.js, re-reads /cart.js,
                                dispatches document "lp:bundle:update" {count, tier, subtotal, saving, tiers, cart},
                                then opens the theme cart drawer (<cart-drawer>) or goes to the cart page.
                                opts.open === false refreshes the drawer contents without opening it.
     LPBundle.refresh()         reads /cart.js and dispatches the event. Resolves with the summary.
     LPBundle.tierFor(count)    0, then the shopper's tiers as fractions (default 0.15, 0.20, 0.25).
     LPBundle.tiers()           the shopper's three tier percentages, e.g. [15, 20, 25].
     LPBundle.openCart()        opens the drawer, else goes to the cart page.
     LPBundle.money(cents)      formats like the theme's money_without_trailing_zeros.
     LPBundle.setKit(ctx) / getKit() / clearKit()   sessionStorage["lpKit"] kit context.
  2) The landing page UI ([data-lpb-byo], [data-lpb-kits], [data-lpb-scroll]).

  Tiers and the piece count follow assets/zbs-bundle.js (theme Bundle & Save placements), so the page agrees
  with the cart drawer, including the Intelligems tier test (15/20/25 vs 20/25/30). Gift cards, re:do lines,
  _gift lines and anything under $2 do not count as pieces.
*/
(function () {
  'use strict';

  var EVENT = 'lp:bundle:update';
  var KIT_KEY = 'lpKit';
  var DEFAULT_TIERS = [15, 20, 25];

  /* ---------------- shared contract ---------------- */
  if (!window.LPBundle || !window.LPBundle.__lpbFull) {
    var state = { cart: null, summary: null, inflight: null };

    var routeCart = function () { return (window.routes && window.routes.cart_url) || '/cart'; };
    var routeAdd = function () { return (window.routes && window.routes.cart_add_url) || '/cart/add'; };

    var validTiers = function (t) {
      return Array.isArray(t) && t.length === 3 && t.every(function (x) { return isFinite(+x) && +x > 0; });
    };
    var igTiers = function () {
      var C = window.zbsConfig || {};
      if (!C.igExperiment) return null;
      try {
        var u = window.igData && window.igData.user;
        if (!u || typeof u.getVariationName !== 'function') return null;
        var m = /(\d+)\s*\/\s*(\d+)\s*\/\s*(\d+)/.exec(u.getVariationName(C.igExperiment) || '');
        return m ? [+m[1], +m[2], +m[3]] : null;
      } catch (e) { return null; }
    };
    var storedTiers = function () {
      try { var v = JSON.parse(sessionStorage.getItem('zbsTiers') || 'null'); return validTiers(v) ? v : null; } catch (e) { return null; }
    };
    var tiers = function () {
      if (validTiers(window.zBundleTiers)) return window.zBundleTiers.map(Number);
      var C = window.zbsConfig || {};
      var t = igTiers() || storedTiers() || (validTiers(C.tiers) ? C.tiers : null) || DEFAULT_TIERS;
      return t.map(Number);
    };
    var tierFor = function (count) {
      var t = tiers();
      count = +count || 0;
      if (count >= 4) return t[2] / 100;
      if (count === 3) return t[1] / 100;
      if (count === 2) return t[0] / 100;
      return 0;
    };

    var counts = function (it) {
      if (!it || it.gift_card || it.vendor === 're:do' || (it.properties && it.properties._gift)) return false;
      if ((it.original_price || it.price || 0) < 200) return false;
      return true;
    };
    var summarise = function (cart) {
      var count = 0, subtotal = 0, items = [];
      ((cart && cart.items) || []).forEach(function (it) {
        if (!counts(it)) return;
        count += it.quantity;
        subtotal += it.original_line_price != null ? it.original_line_price : it.line_price;
        items.push(it);
      });
      var tier = tierFor(count);
      return { count: count, tier: tier, subtotal: subtotal, saving: Math.round(subtotal * tier), tiers: tiers(), items: items, cart: cart };
    };

    var currency = function () {
      return (window.Shopify && Shopify.currency && Shopify.currency.active) ||
        (window.zbsConfig && window.zbsConfig.currency) || (state.cart && state.cart.currency) || 'AUD';
    };
    var money = function (cents) {
      var v = Math.round(+cents || 0) / 100;
      var whole = Math.round(+cents || 0) % 100 === 0;
      try {
        return new Intl.NumberFormat('en-AU', {
          style: 'currency', currency: currency(), currencyDisplay: 'narrowSymbol',
          minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: whole ? 0 : 2
        }).format(v);
      } catch (e) { return '$' + (whole ? v.toFixed(0) : v.toFixed(2)); }
    };

    var dispatch = function (s) {
      state.summary = s;
      try {
        document.dispatchEvent(new CustomEvent(EVENT, { detail: s }));
      } catch (e) { /* very old browsers */ }
      return s;
    };

    var refresh = function () {
      if (state.inflight) return state.inflight;
      state.inflight = fetch(routeCart() + '.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) { state.cart = cart; state.inflight = null; return dispatch(summarise(cart)); })
        .catch(function (err) { state.inflight = null; throw err; });
      return state.inflight;
    };

    var drawer = function () {
      var d = document.querySelector('cart-drawer');
      return d && typeof d.getSectionsToRender === 'function' ? d : null;
    };

    var renderDrawer = function (d, resp, open) {
      if (!d || !resp || !resp.sections) return;
      d.classList.remove('is-empty');
      var inner = d.querySelector('.drawer__inner');
      if (inner) inner.classList.remove('is-empty');
      if (open !== false && typeof d.renderContents === 'function') { d.renderContents(resp); return; }
      d.getSectionsToRender().forEach(function (s) {
        var html = resp.sections[s.id];
        if (!html) return;
        var el = s.selector ? document.querySelector(s.selector) : document.getElementById(s.id);
        if (!el) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var src = doc.querySelector(s.selector || '.shopify-section');
        if (src) el.innerHTML = src.innerHTML;
      });
      var overlay = d.querySelector('#CartDrawer-Overlay');
      if (overlay && typeof d.close === 'function') overlay.addEventListener('click', d.close.bind(d));
    };

    var openCart = function () {
      var d = drawer();
      if (d && typeof d.open === 'function') {
        if (d.classList.contains('is-empty') && state.summary && state.summary.count) {
          window.location.href = routeCart();
          return;
        }
        d.open();
        return;
      }
      window.location.href = routeCart();
    };

    var add = function (items, opts) {
      opts = opts || {};
      var d = drawer();
      var body = { items: (items || []).map(function (i) { return { id: +i.id, quantity: +i.quantity || 1, properties: i.properties }; }) };
      if (d) {
        body.sections = d.getSectionsToRender().map(function (s) { return s.id; });
        body.sections_url = window.location.pathname;
      }
      return fetch(routeAdd() + '.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body)
      })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, json: j }; }); })
        .then(function (res) {
          var j = res.json || {};
          if (!res.ok || j.status) {
            var err = new Error(j.description || j.message || 'Could not add to bag');
            err.response = j;
            throw err;
          }
          if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
            try { window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'lp-bundle', cartData: j }); } catch (e) {}
          }
          if (d && j.sections) renderDrawer(d, j, opts.open);
          return refresh().then(function (s) {
            if (opts.open !== false && !d) window.location.href = routeCart();
            return s;
          });
        });
    };

    var setKit = function (ctx) { try { sessionStorage.setItem(KIT_KEY, JSON.stringify(ctx)); } catch (e) {} };
    var getKit = function () { try { return JSON.parse(sessionStorage.getItem(KIT_KEY) || 'null'); } catch (e) { return null; } };
    var clearKit = function () { try { sessionStorage.removeItem(KIT_KEY); } catch (e) {} };

    window.LPBundle = {
      __lpbFull: true,
      EVENT: EVENT, KIT_KEY: KIT_KEY,
      add: add, refresh: refresh, tierFor: tierFor, tiers: tiers, summarise: summarise,
      openCart: openCart, money: money, setKit: setKit, getKit: getKit, clearKit: clearKit,
      get summary() { return state.summary; }
    };

    // Keep in step with cart changes made elsewhere (drawer quantity, remove, upsell adds).
    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS) {
      try {
        window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function (e) {
          if (e && e.source === 'lp-bundle') return;
          setTimeout(function () { refresh().catch(function () {}); }, 250);
        });
      } catch (e) {}
    }
  }

  var LP = window.LPBundle;

  /* ---------------- page UI ---------------- */
  function $(s, r) { return (r || document).querySelector(s); }
  function $$(s, r) { return Array.prototype.slice.call((r || document).querySelectorAll(s)); }
  // Swap a tile image to a colour, keeping the flat lay / photo treatment in step.
  function setImg(img, c) {
    if (!img || !c || !c.image) return;
    img.removeAttribute('srcset');
    img.src = c.image;
    img.classList.toggle('is-flat', !!c.flat);
  }
  function pct(f) { return Math.round(f * 100) + '%'; }
  function fill(tpl, map) {
    return String(tpl || '').replace(/\[(\w+)\]/g, function (m, k) { return map[k] != null ? map[k] : m; });
  }

  // Smooth scroll for hero / FAQ anchors.
  function initScroll() {
    $$('[data-lpb-scroll]').forEach(function (a) {
      if (a.__lpb) return; a.__lpb = true;
      a.addEventListener('click', function (e) {
        var id = (a.getAttribute('href') || '').replace(/^#/, '');
        var t = id && document.getElementById(id);
        if (!t) return;
        e.preventDefault();
        var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        t.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
        if (history.replaceState) history.replaceState(null, '', '#' + id);
      });
    });
  }

  // Shopper's tier group differs from the copy (Intelligems): retune percentages in tier copy and kit prices.
  function applyTiers() {
    var t = LP.tiers();
    var same = t[0] === DEFAULT_TIERS[0] && t[1] === DEFAULT_TIERS[1] && t[2] === DEFAULT_TIERS[2];
    var map = {}; map[DEFAULT_TIERS[0]] = t[0]; map[DEFAULT_TIERS[1]] = t[1]; map[DEFAULT_TIERS[2]] = t[2];
    $$('.lpb-hero__sub, .lpb-intro__text, .lpb-tiers__value, .lpb-faq__item:first-child .lpb-faq__a, .lpb-progress__label').forEach(function (el) {
      if (el.__lpbText == null) el.__lpbText = el.innerHTML;
      el.innerHTML = same ? el.__lpbText : el.__lpbText.replace(/\b(15|20|25)%/g, function (m, n) { return map[n] + '%'; });
    });
    $$('[data-lpb-kit-total]').forEach(function (k) {
      var total = +k.getAttribute('data-lpb-kit-total') || 0;
      var n = +k.getAttribute('data-lpb-kit-n') || 0;
      var fixed = +k.getAttribute('data-lpb-kit-tier') || 0;
      if (fixed || !total) return;
      var tier = LP.tierFor(n);
      var saving = Math.round(total * tier);
      var now = $('[data-lpb-kit-now]', k), save = $('[data-lpb-kit-save]', k), pctEl = $('[data-lpb-kit-pct]', k);
      if (now) now.textContent = LP.money(total - saving);
      if (save && save.getAttribute('data-tpl')) save.textContent = save.getAttribute('data-tpl').replace('[amount]', LP.money(saving));
      if (pctEl) pctEl.textContent = Math.round(tier * 100) + '%';
    });
  }

  function initKits(root) {
    if (root.__lpb) return; root.__lpb = true;
    var track = $('[data-lpb-kit-track]', root);
    var dots = $$('[data-lpb-dots] span', root);
    if (!track || !dots.length) return;
    var tick = null;
    track.addEventListener('scroll', function () {
      if (tick) return;
      tick = requestAnimationFrame(function () {
        tick = null;
        var cards = $$('.lpb-kit', track);
        var left = track.scrollLeft, best = 0, bestD = Infinity;
        cards.forEach(function (c, i) {
          var d = Math.abs(c.offsetLeft - track.offsetLeft - left - parseFloat(getComputedStyle(track).paddingLeft || 0));
          if (d < bestD) { bestD = d; best = i; }
        });
        dots.forEach(function (d, i) { d.classList.toggle('is-on', i === best); });
      });
    }, { passive: true });
  }

  function initByo(root) {
    if (root.__lpb) return; root.__lpb = true;
    var D = root.dataset;
    var showCount = +D.showCount || 6;
    var cards = $$('[data-lpb-card]', root);
    var more = $('[data-lpb-more]', root);
    var empty = $('[data-lpb-empty]', root);
    var filter = 'all';
    var byProduct = {}; // product id -> {card, colour, fit}

    var seen = {}; // product ids already on the page (every colour), for de-duplicating loaded cards
    var moreUrl = D.moreUrl || '';
    var morePage = 0, morePages = moreUrl ? Infinity : 0, loading = null;
    var limit = showCount;

    function initCard(card) {
      var data = {};
      try { data = JSON.parse($('[data-lpb-card-data]', card).textContent); } catch (e) { data = { fits: { womens: [], mens: [] } }; }
      card.__lpbData = data;
      ['womens', 'mens'].forEach(function (fit) {
        (data.fits[fit] || []).forEach(function (c) { byProduct[c.id] = { card: card, colour: c, fit: fit, title: data.title }; seen[c.id] = true; });
      });
      (card.getAttribute('data-ids') || '').split(' ').forEach(function (id) { if (id) seen[id] = true; });
      card.__lpbFit = card.getAttribute('data-fit0') || 'womens';
      card.__lpbColour = (data.fits[card.__lpbFit] || [])[0] || null;

      // Card swatches swap the card image, link and price.
      $$('[data-lpb-colour]', card).forEach(function (sw) {
        sw.addEventListener('click', function () {
          var hit = byProduct[sw.getAttribute('data-lpb-colour')];
          if (!hit) return;
          card.__lpbColour = hit.colour;
          card.__lpbFit = hit.fit;
          setTag(card);
          $$('[data-lpb-colour]', card).forEach(function (o) { o.setAttribute('aria-pressed', o === sw ? 'true' : 'false'); });
          var img = $('.lpb-card__media img', card);
          setImg(img, hit.colour);
          $$('[data-lpb-card-link]', card).forEach(function (a) { a.href = hit.colour.url; });
          var price = $('[data-lpb-card-price]', card);
          if (price) price.textContent = LP.money(hit.colour.price);
        });
      });
      $('[data-lpb-open]', card).addEventListener('click', function (e) { openQA(card, e.currentTarget); });
    }
    cards.forEach(initCard);

    /* Filters + View more */
    function matches(card) {
      if (filter === 'all') return true;
      var kind = card.getAttribute('data-kind');
      if (filter === 'gear') return kind === 'gear';
      if (kind === 'gear') return false;
      return (' ' + card.getAttribute('data-fits') + ' ').indexOf(' ' + filter + ' ') > -1;
    }
    function matchingCount() { return cards.filter(matches).length; }
    function exhausted() { return morePage >= morePages; }
    function layout() {
      var shown = 0, matching = 0;
      cards.forEach(function (card) {
        var ok = matches(card);
        if (ok) matching++;
        var visible = ok && shown < limit;
        if (visible) shown++;
        card.hidden = !visible;
      });
      if (more) more.hidden = matching <= limit && exhausted();
      if (empty) empty.hidden = matching > 0 || !exhausted();
    }
    // Next page of the collection via the Section Rendering API (sections/lp-bundle-byo-more.liquid).
    function loadPage() {
      if (loading) return loading;
      if (exhausted()) return Promise.resolve(0);
      var next = morePage + 1;
      var url = moreUrl + (moreUrl.indexOf('?') > -1 ? '&' : '?') + 'section_id=lp-bundle-byo-more&page=' + next;
      loading = fetch(url, { credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var meta = $('[data-lpb-more-meta]', doc);
          morePage = next;
          morePages = meta ? (+meta.getAttribute('data-pages') || next) : next;
          var grid = $('[data-lpb-grid]', root), added = 0;
          $$('[data-lpb-card]', doc).forEach(function (c) {
            var ids = (c.getAttribute('data-ids') || '').split(' ').filter(Boolean);
            if (!ids.length || ids.some(function (id) { return seen[id]; })) return;
            var card = document.importNode(c, true);
            card.hidden = true;
            grid.appendChild(card);
            cards.push(card);
            initCard(card);
            added++;
          });
          loading = null;
          if (added && LP.summary) document.dispatchEvent(new CustomEvent(EVENT, { detail: LP.summary }));
          return added;
        })
        .catch(function () { loading = null; morePages = morePage; return 0; });
      return loading;
    }
    // Load pages until `limit` matching cards exist, the collection runs out, or maxPages pages are fetched.
    function fillGrid(maxPages) {
      if (matchingCount() >= limit || exhausted() || maxPages <= 0) { layout(); return Promise.resolve(); }
      if (more) more.setAttribute('aria-busy', 'true');
      return loadPage().then(function () {
        if (more) more.removeAttribute('aria-busy');
        return fillGrid(maxPages - 1);
      });
    }
    $$('[data-lpb-filter]', root).forEach(function (chip) {
      chip.addEventListener('click', function () {
        filter = chip.getAttribute('data-lpb-filter');
        $$('[data-lpb-filter]', root).forEach(function (c) { c.setAttribute('aria-pressed', c === chip ? 'true' : 'false'); });
        if (filter === 'womens' || filter === 'mens' || filter === 'all') {
          cards.forEach(function (card) {
            // All shows each card's own starting fit (a womens/mens mix); Womens/Mens switch every card that has it.
            var want = filter === 'all' ? (card.getAttribute('data-fit0') || 'womens') : filter;
            var list = card.__lpbData.fits[want] || [];
            if (list.length && card.__lpbFit !== want) {
              card.__lpbFit = want; card.__lpbColour = list[0];
              var img = $('.lpb-card__media img', card);
              setImg(img, list[0]);
              $$('[data-lpb-card-link]', card).forEach(function (a) { a.href = list[0].url; });
              var price = $('[data-lpb-card-price]', card);
              if (price) price.textContent = LP.money(list[0].price);
              renderCardSwatches(card);
              setTag(card);
            }
          });
        }
        limit = showCount;
        layout();
        fillGrid(3);
      });
    });
    function setTag(card) {
      var tag = $('[data-lpb-card-tag]', card);
      if (tag) tag.textContent = (card.__lpbColour && /^unisex-daylight-technical-/.test(card.__lpbColour.handle || '')) ? 'UNISEX' : (card.__lpbFit === 'mens' ? 'MENS' : 'WOMENS');
    }
    function renderCardSwatches(card) {
      var wrap = $('[data-lpb-card-swatches]', card);
      if (!wrap) return;
      var list = (card.__lpbData.fits[card.__lpbFit] || []).slice(0, 6);
      wrap.innerHTML = '';
      list.forEach(function (c) {
        var b = document.createElement('button');
        b.type = 'button'; b.className = 'lpb-swatch'; b.style.backgroundColor = c.hex;
        b.setAttribute('aria-label', c.name); b.setAttribute('data-lpb-colour', c.id);
        b.setAttribute('aria-pressed', card.__lpbColour && card.__lpbColour.id === c.id ? 'true' : 'false');
        b.addEventListener('click', function () {
          card.__lpbColour = c;
          $$('[data-lpb-colour]', wrap).forEach(function (o) { o.setAttribute('aria-pressed', o === b ? 'true' : 'false'); });
          var img = $('.lpb-card__media img', card);
          setImg(img, c);
          $$('[data-lpb-card-link]', card).forEach(function (a) { a.href = c.url; });
        });
        wrap.appendChild(b);
      });
    }
    var moreBtn = $('[data-lpb-more-btn]', root);
    if (moreBtn) moreBtn.addEventListener('click', function () {
      var before = cards.filter(function (c) { return !c.hidden; }).length;
      limit += showCount;
      moreBtn.disabled = true;
      fillGrid(5).then(function () {
        moreBtn.disabled = false;
        layout();
        var firstNew = cards.filter(function (c) { return !c.hidden; })[before];
        if (firstNew) { var b = $('[data-lpb-open]', firstNew); if (b) b.focus({ preventScroll: true }); }
      });
    });
    layout();

    /* Quick add */
    var qa = $('[data-lpb-qa]', root);
    var panel = $('[data-lpb-qa-panel]', root);
    var qaState = { card: null, fit: 'womens', colour: null, variant: null, opener: null };
    var addBtn = $('[data-lpb-qa-add]', qa);
    var qaAdding = false;
    var errEl = $('[data-lpb-qa-error]', qa);
    var mobile = function () { return window.matchMedia('(max-width: 749px)').matches; };

    function currentCount() { return (LP.summary && LP.summary.count) || 0; }

    function renderQA() {
      var data = qaState.card.__lpbData;
      var list = data.fits[qaState.fit] || [];
      var c = qaState.colour;
      $$('[data-lpb-qa-url]', qa).forEach(function (a) { a.href = c.url; });
      var img = $('[data-lpb-qa-img]', qa);
      if (c.thumb || c.image) { setImg(img, c); img.alt = data.title + ' ' + c.name; }
      $('[data-lpb-qa-title]', qa).textContent = data.title;
      $('[data-lpb-qa-price]', qa).textContent = LP.money(qaState.variant ? qaState.variant.price : c.price);

      var fitWrap = $('[data-lpb-fit]', qa);
      var hasBoth = (data.fits.womens || []).length && (data.fits.mens || []).length;
      fitWrap.hidden = !hasBoth;
      $$('[data-lpb-fit-btn]', qa).forEach(function (b) { b.setAttribute('aria-pressed', b.getAttribute('data-lpb-fit-btn') === qaState.fit ? 'true' : 'false'); });

      var colWrap = $('[data-lpb-qa-colours]', qa);
      colWrap.hidden = list.length < 1 || (list.length < 2 && !c.name);
      $('[data-lpb-qa-colour-name]', qa).textContent = c.name;
      var sw = $('[data-lpb-qa-swatches]', qa);
      sw.innerHTML = '';
      list.forEach(function (col) {
        var b = document.createElement('button');
        b.type = 'button'; b.className = 'lpb-swatch'; b.style.backgroundColor = col.hex;
        b.setAttribute('aria-label', col.name);
        b.setAttribute('aria-pressed', col.id === c.id ? 'true' : 'false');
        b.addEventListener('click', function () { qaState.colour = col; pickDefaultVariant(); renderQA(); });
        sw.appendChild(b);
      });

      var sizeWrap = $('[data-lpb-qa-sizes]', qa);
      var sizes = $('[data-lpb-qa-size-list]', qa);
      sizeWrap.hidden = !c.sized;
      sizes.innerHTML = '';
      if (c.sized) {
        c.variants.forEach(function (v) {
          var b = document.createElement('button');
          b.type = 'button'; b.className = 'lpb-size'; b.textContent = v.size;
          b.disabled = !v.available;
          if (!v.available) b.setAttribute('aria-label', v.size + ', sold out');
          b.setAttribute('aria-pressed', qaState.variant && qaState.variant.id === v.id ? 'true' : 'false');
          b.addEventListener('click', function () { qaState.variant = v; errEl.textContent = ''; renderQA(); });
          sizes.appendChild(b);
        });
      }

      var n = currentCount();
      var nudge = $('[data-lpb-qa-nudge]', qa);
      var next = LP.tierFor(n + 1);
      if (next > LP.tierFor(n)) {
        var price = qaState.variant ? qaState.variant.price : c.price;
        var subtotal = ((LP.summary && LP.summary.subtotal) || 0) + price;
        nudge.innerHTML = '<span class="lpb-nudge__copy"><strong class="lpb-nudge__title"></strong><span class="lpb-nudge__saving"></span></span>';
        $('.lpb-nudge__title', nudge).textContent = 'Add this item to unlock ' + pct(next) + ' off your bundle.';
        $('.lpb-nudge__saving', nudge).textContent = (n + 1) + ' items · You save ' + LP.money(Math.round(subtotal * next));
        nudge.hidden = false;
      } else { nudge.hidden = true; }

      var ready = !!qaState.variant;
      var soldOut = !c.variants.some(function (v) { return v.available; });
      addBtn.disabled = qaAdding || !ready || soldOut;
      addBtn.textContent = ready ? fill(D.addLabel, { price: LP.money(qaState.variant.price) }) : D.pickSize;
      if (soldOut) addBtn.textContent = fill(D.addLabel, { price: LP.money(c.price) });
    }
    function pickDefaultVariant() {
      var c = qaState.colour;
      qaState.variant = null;
      if (!c) return;
      if (!c.sized) {
        qaState.variant = c.variants.filter(function (v) { return v.available; })[0] || null;
      }
    }
    function position(opener) {
      if (mobile()) { panel.style.left = ''; panel.style.top = ''; return; }
      var r = (opener.closest('[data-lpb-card]') || opener).getBoundingClientRect();
      var w = panel.offsetWidth, h = panel.offsetHeight;
      var vw = document.documentElement.clientWidth, vh = window.innerHeight;
      var left = r.right + 16;
      if (left + w > vw - 16) left = r.left - w - 16;
      if (left < 16) left = Math.max(16, Math.min(vw - w - 16, r.left + (r.width - w) / 2));
      var top = Math.max(16, Math.min(vh - h - 16, r.top));
      panel.style.left = left + 'px';
      panel.style.top = top + 'px';
    }
    function openQA(card, opener) {
      var data = card.__lpbData;
      var fit = card.__lpbFit;
      if (!(data.fits[fit] || []).length) fit = (data.fits.womens || []).length ? 'womens' : 'mens';
      qaState.card = card; qaState.fit = fit; qaState.opener = opener;
      qaState.colour = card.__lpbColour && (data.fits[fit] || []).some(function (c) { return c.id === card.__lpbColour.id; })
        ? card.__lpbColour : (data.fits[fit] || [])[0];
      if (!qaState.colour) return;
      errEl.textContent = '';
      pickDefaultVariant();
      renderQA();
      qa.classList.add('is-open');
      document.documentElement.style.overflow = mobile() ? 'hidden' : '';
      position(opener);
      panel.focus({ preventScroll: true });
    }
    function closeQA() {
      if (!qa.classList.contains('is-open')) return;
      qa.classList.remove('is-open');
      document.documentElement.style.overflow = '';
      if (qaState.opener) qaState.opener.focus({ preventScroll: true });
    }
    $$('[data-lpb-qa-close]', qa).forEach(function (b) { b.addEventListener('click', closeQA); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeQA();
      if (e.key === 'Tab' && qa.classList.contains('is-open')) {
        var f = $$('a[href], button:not([disabled])', panel).filter(function (el) { return el.offsetParent !== null && el.tabIndex !== -1; });
        if (!f.length) return;
        if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
        else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
      }
    });
    window.addEventListener('resize', function () { if (qa.classList.contains('is-open') && qaState.opener) position(qaState.opener); });
    window.addEventListener('scroll', function () { if (qa.classList.contains('is-open') && !mobile()) closeQA(); }, { passive: true });
    $$('[data-lpb-fit-btn]', qa).forEach(function (b) {
      b.addEventListener('click', function () {
        var fit = b.getAttribute('data-lpb-fit-btn');
        var list = qaState.card.__lpbData.fits[fit] || [];
        if (!list.length) return;
        // keep the same colour name across fits when it exists
        var same = list.filter(function (c) { return qaState.colour && c.name === qaState.colour.name; })[0];
        qaState.fit = fit; qaState.colour = same || list[0];
        pickDefaultVariant(); renderQA();
      });
    });
    addBtn.addEventListener('click', function () {
      if (qaAdding || !qaState.variant || addBtn.disabled) return;
      var variantId = qaState.variant.id;
      var card = qaState.card;
      qaAdding = true;
      addBtn.disabled = true;
      addBtn.classList.add('is-loading');
      addBtn.setAttribute('aria-busy', 'true');
      errEl.textContent = '';
      LP.add([{ id: variantId, quantity: 1 }], { open: false })
        .then(function () {
          if (qaState.card === card && qaState.variant && qaState.variant.id === variantId) closeQA();
        })
        .catch(function (err) { errEl.textContent = err.message; })
        .then(function () {
          qaAdding = false;
          addBtn.classList.remove('is-loading');
          addBtn.removeAttribute('aria-busy');
          renderQA();
        });
    });

    /* Panel + sticky bar */
    var panelEl = $('[data-lpb-panel]', root);
    var bar = $('[data-lpb-bar-mobile]', root);
    var inView = false;
    function render(s) {
      var t = s.tiers || LP.tiers();
      var n = s.count;
      var cur = s.tier, next = LP.tierFor(n + 1);
      var msg;
      if (n === 0) msg = D.msgEmpty;
      else if (cur === 0) msg = fill(D.msgOne, { next: pct(next) });
      else if (next > cur) msg = fill(D.msgNext, { current: pct(cur), next: pct(next) });
      else msg = fill(D.msgMax, { current: pct(cur) });
      var barMsg = (n > 0 && next > cur) ? fill(D.msgOne, { next: pct(next) }) : msg;
      $$('[data-lpb-msg]', root).forEach(function (el) { el.textContent = el.closest('[data-lpb-bar-mobile]') ? barMsg : msg; });
      $$('[data-lpb-bar]', root).forEach(function (b) { b.classList.toggle('is-on', n >= +b.getAttribute('data-lpb-bar')); });
      $$('[data-lpb-bar-label]', root).forEach(function (b) { b.classList.toggle('is-on', n >= +b.getAttribute('data-lpb-bar-label')); });
      $$('[data-lpb-was]', root).forEach(function (el) { el.textContent = s.saving > 0 ? LP.money(s.subtotal) : ''; });
      $$('[data-lpb-now]', root).forEach(function (el) { el.textContent = LP.money(s.subtotal - s.saving); });
      $$('[data-lpb-checkout]', root).forEach(function (b) { b.setAttribute('aria-disabled', n ? 'false' : 'true'); });

      var countEl = $('[data-lpb-bar-count]', root);
      if (countEl) {
        var ct = fill(D.barCount, { count: n, current: pct(cur) });
        if (cur === 0) ct = ct.replace(/\s*·\s*0%.*$/, '');
        if (n === 1) ct = ct.replace(/\bpieces\b/, 'piece');
        countEl.textContent = ct;
      }

      // Lines
      var lines = $('[data-lpb-lines]', root);
      if (lines) {
        lines.innerHTML = '';
        s.items.forEach(function (it) {
          var hit = byProduct[it.product_id];
          var row = document.createElement('div'); row.className = 'lpb-line';
          var img = document.createElement('img'); img.className = 'lpb-line__img'; img.alt = ''; img.width = 56; img.height = 56; img.loading = 'lazy';
          var src = (hit && hit.colour.thumb) || it.image || (it.featured_image && it.featured_image.url);
          if (src) img.src = src;
          img.classList.toggle('is-flat', !!(hit && hit.colour.thumb && hit.colour.flat));
          var info = document.createElement('div'); info.className = 'lpb-line__info';
          var name = document.createElement('span'); name.className = 'lpb-line__name';
          name.textContent = hit ? hit.title : it.product_title;
          var meta = document.createElement('span'); meta.className = 'lpb-line__meta';
          var bits = [];
          if (hit) bits.push(hit.colour.name);
          if (it.variant_title && it.variant_title !== 'Default Title') bits.push(it.variant_title);
          if (it.quantity > 1) bits.push('x' + it.quantity);
          meta.textContent = bits.join(' · ');
          var price = document.createElement('span'); price.className = 'lpb-line__price';
          price.textContent = LP.money(it.original_line_price != null ? it.original_line_price : it.line_price);
          info.appendChild(name); if (bits.length) info.appendChild(meta);
          row.appendChild(img); row.appendChild(info); row.appendChild(price);
          lines.appendChild(row);
        });
      }
      var saving = $('[data-lpb-saving]', root);
      if (saving) {
        saving.hidden = !(s.saving > 0);
        if (!saving.__tpl) saving.__tpl = $('[data-lpb-saving-label]', saving).textContent;
        $('[data-lpb-saving-label]', saving).textContent = fill(saving.__tpl, { current: pct(cur) });
        $('[data-lpb-saving-value]', saving).textContent = '−' + LP.money(s.saving);
      }

      // Added state on cards
      var inCart = {};
      s.items.forEach(function (it) { inCart[it.product_id] = true; });
      cards.forEach(function (card) {
        var data = card.__lpbData, added = false;
        ['womens', 'mens'].forEach(function (f) { (data.fits[f] || []).forEach(function (c) { if (inCart[c.id]) added = true; }); });
        card.classList.toggle('is-added', added);
        var b = $('[data-lpb-open]', card);
        if (b) b.textContent = added ? D.addedLabel : D.addCardLabel;
      });

      if (bar) bar.classList.toggle('is-visible', inView && n > 0);
      if (qa.classList.contains('is-open')) renderQA();
      applyTiers();
    }
    document.addEventListener(EVENT, function (e) { render(e.detail); });
    if (LP.summary) render(LP.summary);

    $$('[data-lpb-checkout]', root).forEach(function (b) {
      b.addEventListener('click', function () {
        if (b.getAttribute('aria-disabled') === 'true') {
          var grid = $('[data-lpb-grid]', root);
          if (grid) grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
        LP.openCart();
      });
    });

    if (bar && 'IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        inView = entries[0].isIntersecting;
        bar.classList.toggle('is-visible', inView && currentCount() > 0);
      }, { rootMargin: '0px 0px -40% 0px' }).observe($('.lpb-byo__body', root) || root);
    }
    if (panelEl) panelEl.setAttribute('data-ready', '');
  }

  function init() {
    initScroll();
    $$('[data-lpb-byo]').forEach(initByo);
    $$('[data-lpb-kits]').forEach(initKits);
    applyTiers();
    LP.refresh().catch(function () {});
    // Intelligems can assign the tier group after load.
    setTimeout(applyTiers, 1200);
    setTimeout(function () { if (LP.summary) document.dispatchEvent(new CustomEvent(EVENT, { detail: LP.summarise(LP.summary.cart) })); }, 3000);
  }
  if (window.__lpbBooted) return;
  window.__lpbBooted = true;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', function () { initScroll(); $$('[data-lpb-byo]').forEach(initByo); $$('[data-lpb-kits]').forEach(initKits); applyTiers(); LP.refresh().catch(function () {}); });
})();
